import React from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { DropDownArrowIcon } from "@ippon/shared-assets";
import { InputModeType } from "../TextInput/CustomTextInput";
import { useResponsiveMenuPlacement } from "../../hooks/useResponsiveMenuPlacement";

interface CustomTextFieldProps {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  sx?: any;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  inputMode?: InputModeType;
}

const CustomTextField = ({
  value,
  onChange,
  sx,
  placeholder,
  disabled,
  fullWidth,
  inputMode,
}: CustomTextFieldProps) => {
  return (
    <TextField
      value={value || ""}
      onChange={onChange}
      variant="outlined"
      size="small"
      placeholder={placeholder}
      disabled={disabled}
      fullWidth={fullWidth}
      inputProps={{ inputMode: inputMode }}
      sx={{
        "& .MuiInputBase-root": {
          height: "34px",
          fontSize: "12px",
          backgroundColor: "#ffffff",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#D9D9D9",
            borderRadius: "8px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#D9D9D9",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#D9D9D9 !important",
            borderWidth: "1px",
          },
          "&.Mui-focused": {
            outline: "none",
            boxShadow: "none",
          },
        },
        "& .MuiInputBase-input": {
          fontSize: "12px",
          padding: "0 14px",
          height: "100%",
          "&::placeholder": {
            color: "#989898",
            opacity: 0.8,
          },
        },
        ...sx,
      }}
    />
  );
};

interface CustomThreeColInputGroupProps {
  title?: string;
  firstPlaceholder: string;
  secondPlaceholder: string;
  thirdPlaceholder: string;
  firstLabel: string;
  secondLabel: string;
  thirdLabel: string;
  secondUnit?: string;
  thirdUnit?: string;
  secondFieldType?: "text" | "select";
  secondFieldOptions?: Array<{ label: string; value: string }>;
  disabled?: boolean;
  isRequired?: boolean;
  index?: number;
  onRemove?: () => void;
  values: {
    first: string;
    second: string;
    third: string;
  };
  onChange: (field: "first" | "second" | "third", value: string) => void;
  validationPatterns?: {
    first?: RegExp;
    second?: RegExp;
    third?: RegExp;
  };
  inputModes?: {
    first?: InputModeType;
    second?: InputModeType;
    third?: InputModeType;
  };
  inputWidth?: string;
}

export const RequireIcon = () => (
  <svg
    width="11"
    height="20"
    viewBox="0 0 11 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.56818 13.75L1.56818 12.7273L7.65909 6.52273L8.65909 7.54545L2.56818 13.75ZM7.65909 13.75L1.56818 7.54545L2.56818 6.52273L8.65909 12.7273L7.65909 13.75ZM0.818182 10.0909C0.818182 9.84091 0.892045 9.63068 1.03977 9.46023C1.1875 9.28977 1.40909 9.20455 1.70455 9.20455C2.01136 9.20455 2.23864 9.28977 2.38636 9.46023C2.53788 9.63068 2.61364 9.84091 2.61364 10.0909C2.61364 10.3295 2.53788 10.5303 2.38636 10.6932C2.23864 10.8523 2.01136 10.9318 1.70455 10.9318C1.40909 10.9318 1.1875 10.8523 1.03977 10.6932C0.892045 10.5303 0.818182 10.3295 0.818182 10.0909ZM4.15909 13.5227C4.15909 13.2841 4.23295 13.0814 4.38068 12.9148C4.52841 12.7443 4.75 12.6591 5.04545 12.6591C5.34091 12.6591 5.56629 12.7443 5.72159 12.9148C5.87689 13.0814 5.95455 13.2841 5.95455 13.5227C5.95455 13.7614 5.87689 13.9659 5.72159 14.1364C5.56629 14.303 5.34091 14.3864 5.04545 14.3864C4.75 14.3864 4.52841 14.303 4.38068 14.1364C4.23295 13.9659 4.15909 13.7614 4.15909 13.5227ZM4.15909 6.75C4.15909 6.51136 4.23295 6.30871 4.38068 6.14205C4.52841 5.97159 4.75 5.88636 5.04545 5.88636C5.34091 5.88636 5.56629 5.97159 5.72159 6.14205C5.87689 6.30871 5.95455 6.51136 5.95455 6.75C5.95455 6.98864 5.87689 7.19318 5.72159 7.36364C5.56629 7.5303 5.34091 7.61364 5.04545 7.61364C4.75 7.61364 4.52841 7.5303 4.38068 7.36364C4.23295 7.19318 4.15909 6.98864 4.15909 6.75ZM7.47727 10.0909C7.47727 9.84091 7.55492 9.63068 7.71023 9.46023C7.86553 9.28977 8.09091 9.20455 8.38636 9.20455C8.68182 9.20455 8.9072 9.28977 9.0625 9.46023C9.2178 9.63068 9.29545 9.84091 9.29545 10.0909C9.29545 10.3295 9.2178 10.5303 9.0625 10.6932C8.9072 10.8523 8.68182 10.9318 8.38636 10.9318C8.09091 10.9318 7.86553 10.8523 7.71023 10.6932C7.55492 10.5303 7.47727 10.3295 7.47727 10.0909Z"
      fill="#DA384C"
    />
  </svg>
);

const CustomThreeColInputGroup = ({
  title,
  firstPlaceholder,
  secondPlaceholder,
  thirdPlaceholder,
  firstLabel,
  secondLabel,
  thirdLabel,
  secondUnit,
  thirdUnit,
  secondFieldType = "text",
  secondFieldOptions = [],
  disabled = false,
  isRequired = false,
  index,
  onRemove,
  values,
  onChange,
  validationPatterns,
  inputModes,
  inputWidth = "100px",
}: CustomThreeColInputGroupProps) => {
  const {
    menuMaxHeight,
    menuAnchorOrigin,
    menuTransformOrigin,
    updateMenuPlacement,
  } = useResponsiveMenuPlacement(224);
  const menuProps = {
    anchorOrigin: menuAnchorOrigin,
    transformOrigin: menuTransformOrigin,
    PaperProps: { style: { maxHeight: menuMaxHeight } },
  };

  return (
    <Box sx={{ mb: 1 }}>
      {/* Desktop Layout */}
      <Box
        display={{ xs: "none", sm: "block" }}
        sx={{ width: "100%", maxWidth: "600px" }}
      >
        {/* Title */}
        {title && index === 0 && (
          <Typography
            color="#3E3E3E"
            fontWeight={700}
            sx={{
              mb: 2,
              fontSize: "12px",
            }}
          >
            {title}
            {isRequired && (
              <span style={{ display: "inline-flex", marginLeft: "4px" }}>
                <RequireIcon />
              </span>
            )}
          </Typography>
        )}

        {/* Input fields */}
        <Box
          display="flex"
          alignItems="end"
          gap={1}
          sx={{ flexWrap: "nowrap", overflow: "hidden" }}
        >
          {/* First field */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              flex: "1 1 0",
            }}
          >
            {(index === undefined || index === 0) && (
              <Typography
                fontSize={12}
                color="#3E3E3E"
                fontWeight={700}
                sx={{ mb: 1 }}
              >
                {firstLabel}
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CustomTextField
                value={values.first || ""}
                onChange={(e) => {
                  const value = validationPatterns?.first
                    ? e.target.value.replace(validationPatterns.first, "")
                    : e.target.value;
                  onChange("first", value);
                }}
                placeholder={firstPlaceholder}
                sx={{ width: "100%", maxWidth: inputWidth }}
                disabled={disabled}
                inputMode={inputModes?.first}
              />
            </Box>
          </Box>

          {/* Second field */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              flex: "1 1 0",
            }}
          >
            {(index === undefined || index === 0) && (
              <Typography
                fontSize={12}
                color="#3E3E3E"
                fontWeight={700}
                sx={{ mb: 1 }}
              >
                {secondLabel}
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {secondFieldType === "select" ? (
                <Select
                  value={values.second || ""}
                  onChange={(e) => onChange("second", e.target.value)}
                  disabled={disabled}
                  displayEmpty
                  onOpen={(event) =>
                    updateMenuPlacement(event.currentTarget as HTMLElement)
                  }
                  MenuProps={menuProps}
                  IconComponent={DropDownArrowIcon}
                  renderValue={(selected) => {
                    if (!selected || selected === "") {
                      return (
                        <span style={{ color: "#989898" }}>
                          {secondPlaceholder}
                        </span>
                      );
                    }
                    const option = secondFieldOptions.find(
                      (opt) => opt.value === selected,
                    );
                    return option?.label || selected;
                  }}
                  sx={{
                    width: "100%",
                    maxWidth: inputWidth,
                    height: "34px",
                    fontSize: "12px",
                    backgroundColor: "#ffffff",
                    "& .MuiSelect-select": {
                      padding: "0 14px",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      color: values.second ? "inherit" : "#989898",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9",
                      borderRadius: "8px",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderWidth: "1px",
                    },
                    "&.Mui-focused": {
                      outline: "none",
                      boxShadow: "none",
                    },
                    ".MuiSelect-icon": {
                      fontSize: "14px",
                      right: "8px",
                      top: "calc(50% + 0px)",
                      transform: "translateY(-50%)",
                    },
                    ".MuiSelect-iconOpen": {
                      transform: "translateY(-50%) rotate(180deg)",
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{ fontSize: "12px", color: "#989898" }}
                  >
                    {secondPlaceholder}
                  </MenuItem>
                  {secondFieldOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{ fontSize: "12px" }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <CustomTextField
                  value={values.second || ""}
                  onChange={(e) => {
                    const value = validationPatterns?.second
                      ? e.target.value.replace(validationPatterns.second, "")
                      : e.target.value;
                    onChange("second", value);
                  }}
                  placeholder={secondPlaceholder}
                  sx={{ width: "100%", maxWidth: inputWidth }}
                  disabled={disabled}
                  inputMode={inputModes?.second}
                />
              )}
              {secondUnit && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#3e3e3e",
                    whiteSpace: "nowrap",
                  }}
                >
                  {secondUnit}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Third field */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              flex: "1 1 0",
            }}
          >
            {(index === undefined || index === 0) && (
              <Typography
                fontSize={12}
                color="#3E3E3E"
                fontWeight={700}
                sx={{ mb: 1 }}
              >
                {thirdLabel}
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CustomTextField
                value={values.third || ""}
                onChange={(e) => {
                  const value = validationPatterns?.third
                    ? e.target.value.replace(validationPatterns.third, "")
                    : e.target.value;
                  onChange("third", value);
                }}
                placeholder={thirdPlaceholder}
                sx={{ width: "100%", maxWidth: inputWidth }}
                disabled={disabled}
                inputMode={inputModes?.third}
              />
              {thirdUnit && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#3e3e3e",
                    whiteSpace: "nowrap",
                  }}
                >
                  {thirdUnit}
                </Typography>
              )}
              {onRemove && index !== undefined && index > 0 && (
                <IconButton
                  onClick={onRemove}
                  sx={{
                    width: "30px",
                    height: "30px",
                    color: "#666",
                  }}
                >
                  <ClearIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Mobile Layout */}
      <Box display={{ xs: "block", sm: "none" }} sx={{ width: "100%" }}>
        <Box
          sx={{
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
            backgroundColor: "#fff",
            position: "relative",
          }}
        >
          {/* Header with title and remove button */}
          {index === 0 && title && (
            <Typography
              fontSize={14}
              color="#3E3E3E"
              fontWeight={700}
              sx={{ mb: 2 }}
            >
              {title}
              {isRequired && (
                <span style={{ display: "inline-flex", marginLeft: "4px" }}>
                  <RequireIcon />
                </span>
              )}
            </Typography>
          )}
          {onRemove && (
            <Box
              component="button"
              type="button"
              onClick={onRemove}
              sx={{
                position: "absolute",
                top: "-8px",
                right: "-8px",
                border: "1px solid #D9D9D9",
                cursor: "pointer",
                padding: "0",
                background: "#fff",
                fontSize: "14px",
                color: "white",
                lineHeight: 1,
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <ClearIcon sx={{ fontSize: "14px", color: "#D9D9D9" }} />
            </Box>
          )}

          {/* First input */}
          <Box mb={2}>
            <Typography
              fontSize={12}
              color="#3E3E3E"
              fontWeight={700}
              sx={{ mb: 1 }}
            >
              {firstLabel}
            </Typography>
            <CustomTextField
              value={values.first || ""}
              onChange={(e) => {
                const value = validationPatterns?.first
                  ? e.target.value.replace(validationPatterns.first, "")
                  : e.target.value;
                onChange("first", value);
              }}
              fullWidth
              placeholder={firstPlaceholder}
              disabled={disabled}
              inputMode={inputModes?.first}
            />
          </Box>

          {/* Second input */}
          <Box mb={2}>
            <Typography
              fontSize={12}
              color="#3E3E3E"
              fontWeight={700}
              sx={{ mb: 1 }}
            >
              {secondLabel}
              {secondUnit && (
                <Typography
                  component="span"
                  sx={{ fontSize: "12px", color: "#3e3e3e", ml: 1 }}
                >
                  {secondUnit}
                </Typography>
              )}
            </Typography>
            {secondFieldType === "select" ? (
              <Select
                value={values.second || ""}
                onChange={(e) => onChange("second", e.target.value)}
                disabled={disabled}
                fullWidth
                displayEmpty
                onOpen={(event) =>
                  updateMenuPlacement(event.currentTarget as HTMLElement)
                }
                MenuProps={menuProps}
                IconComponent={DropDownArrowIcon}
                renderValue={(selected) => {
                  if (!selected || selected === "") {
                    return (
                      <span style={{ color: "#989898" }}>
                        {secondPlaceholder}
                      </span>
                    );
                  }
                  const option = secondFieldOptions.find(
                    (opt) => opt.value === selected,
                  );
                  return option?.label || selected;
                }}
                sx={{
                  height: "34px",
                  fontSize: "12px",
                  backgroundColor: "#ffffff",
                  "& .MuiSelect-select": {
                    padding: "0 14px",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    color: values.second ? "inherit" : "#989898",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#D9D9D9",
                    borderRadius: "8px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#D9D9D9",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#D9D9D9 !important",
                    borderWidth: "1px",
                  },
                  "&.Mui-focused": {
                    outline: "none",
                    boxShadow: "none",
                  },
                  ".MuiSelect-icon": {
                    fontSize: "14px",
                    right: "8px",
                    top: "calc(50% + 0px)",
                    transform: "translateY(-50%)",
                  },
                  ".MuiSelect-iconOpen": {
                    transform: "translateY(-50%) rotate(180deg)",
                  },
                }}
              >
                <MenuItem value="" sx={{ fontSize: "12px", color: "#989898" }}>
                  {secondPlaceholder}
                </MenuItem>
                {secondFieldOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{ fontSize: "12px" }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <CustomTextField
                value={values.second || ""}
                onChange={(e) => {
                  const value = validationPatterns?.second
                    ? e.target.value.replace(validationPatterns.second, "")
                    : e.target.value;
                  onChange("second", value);
                }}
                fullWidth
                placeholder={secondPlaceholder}
                disabled={disabled}
                inputMode={inputModes?.second}
              />
            )}
          </Box>

          {/* Third input */}
          <Box>
            <Typography
              fontSize={12}
              color="#3E3E3E"
              fontWeight={700}
              sx={{ mb: 1 }}
            >
              {thirdLabel}
              {thirdUnit && (
                <Typography
                  component="span"
                  sx={{ fontSize: "12px", color: "#3e3e3e", ml: 1 }}
                >
                  {thirdUnit}
                </Typography>
              )}
            </Typography>
            <CustomTextField
              value={values.third || ""}
              onChange={(e) => {
                const value = validationPatterns?.third
                  ? e.target.value.replace(validationPatterns.third, "")
                  : e.target.value;
                onChange("third", value);
              }}
              fullWidth
              placeholder={thirdPlaceholder}
              disabled={disabled}
              inputMode={inputModes?.third}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomThreeColInputGroup;
